import { ReactNode, createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import {
  ArkNote,
  ServiceWorkerWallet,
  NetworkName,
  SingleKey,
  MnemonicIdentity,
  AssetDetails,
  WalletBalance,
  IVtxoManager,
  IndexedDBWalletRepository,
  IndexedDBContractRepository,
  type Activity,
  type Identity,
  type ServiceWorkerWalletMode,
} from '@arkade-os/sdk'
import {
  clearStorage,
  readWalletFromStorage,
  saveWalletToStorage,
  saveAssetMetadataToStorage,
  readAssetMetadataFromStorage,
  readAllTransactionActivityMetadata,
  CachedAssetDetails,
  ASSET_METADATA_TTL_MS,
  type TransactionActivityMetadata,
} from '../lib/storage'
import { NavigationContext, Pages } from './navigation'
import { getBalance, getUnrolledVtxos, getVtxos, settleVtxos } from '../lib/asp'
import { resolveExits, subtractExitedAssets, type ExitRecord } from '../lib/exitHistory'
import { AspContext } from './asp'
import { AssetsContext } from './assets'
import { NotificationsContext } from './notifications'
import { FlowContext } from './flow'
import { arkNoteInUrl } from '../lib/arknote'
import { deepLinkInUrl } from '../lib/deepLink'
import { assetNameChanged, referencedAssetIds } from '../lib/assets'
import { consoleError } from '../lib/logs'
import { Tx, Vtxo, Wallet } from '../lib/types'
import { activitiesToTxs, getActivities } from '../lib/activityHistory'
import { arkTransactionToTx } from '../lib/transactionHistory'
import { Indexer } from '../lib/indexer'
import { lnSendViews, swapRecordResolver, type LnSendView } from '../lib/swapRecords'
import { clearLnurlPaymentsForReset, createLnurlActivityResolver } from '../lib/lnurlPaymentRepository'
import { createSentActivityResolver, forgetLnurlSends, resumeLnurlConfirmations } from '../lib/lnurlSends'
import { lnurlResyncOnPayment, syncLnurlActivity } from '../lib/lnurlActivitySync'
import { assetSwapRepository, type WalletAssetSwap } from '../lib/swapRepository'
import { nsecToPrivateKey, getPrivateKey, noUserDefinedPassword } from '../lib/privateKey'
import { hasMnemonic, getMnemonic, deriveNostrKeyFromMnemonic } from '../lib/mnemonic'
import { resolveWalletMode } from '../lib/walletMode'
import { calcBatchLifetimeMs, calcNextRollover } from '../lib/wallet'
import { setLoadingStatus } from '../lib/loadingStatus'
import { hex } from '@scure/base'
import { secp256k1 } from '@noble/curves/secp256k1.js'
import { ConfigContext } from './config'
import { defaultPassword, getDelegateUrlForNetwork, isMainnet, maxPercentage } from '../lib/constants'
import { AssetIconApprovalManager } from '../lib/assetIconApproval'
import { BackupContext } from './backup'
import { getAssetSwaps } from '@arkade-os/swap/protocol'
import { useTranslation } from '../providers/language'
import { isValidUrl } from '@/lib/validators'

const SERVICE_WORKER_ACTIVATION_TIMEOUT_MS = 5_000
const MESSAGE_BUS_INIT_TIMEOUT_MS = 30_000
const DEV_AUTO_INIT_TIMEOUT_MS = 60_000
const DEV_INITIAL_DATA_TIMEOUT_MS = 20_000
const DEV_AUTO_INIT_RELOAD_KEY = 'arkade-dev-auto-init-reload-attempted'

interface InitSvcWorkerWalletParams {
  arkServerUrl: string
  identity: Identity
  retryCount?: number
  maxRetries?: number
  delegateUrl?: string
  walletMode?: ServiceWorkerWalletMode
  restoring?: boolean
}

const defaultWallet: Wallet = {
  network: '',
  nextRollover: 0,
}

export type WalletAuthState = 'unknown' | 'passwordless' | 'locked' | 'authenticated'

interface WalletContextProps {
  initWallet: (credentials: {
    mnemonic?: string
    privateKey?: Uint8Array
    walletMode?: ServiceWorkerWalletMode
    restoring?: boolean
  }) => Promise<void>
  lockWallet: () => Promise<void>
  resetWallet: () => Promise<void>
  settlePreconfirmed: () => Promise<void>
  unlockWallet: (password: string) => Promise<void>
  updateWallet: (w: Wallet | ((prev: Wallet) => Wallet)) => void
  isLocked: () => Promise<boolean>
  reloadWallet: (svcWallet?: ServiceWorkerWallet) => Promise<void>
  restartWallet: (delegateEnabled?: boolean) => Promise<void>
  wallet: Wallet
  walletLoaded: boolean
  svcWallet: ServiceWorkerWallet | undefined
  vtxoManager: IVtxoManager | undefined
  txs: Tx[]
  /** History rows before grouping, and deliberately not derived from
   * `assetSwaps`. The restore scan takes its candidates from `sent` rows, and
   * `txs` replaces a swap's funding row with the grouped one the moment its
   * record exists — so feeding it `txs` hides the very tx the record was built
   * from, and no later scan can re-answer that record. */
  ungroupedTxs: Tx[]
  /** Set by the asset-swaps provider, which owns the records. This provider
   * merges them into `txs`; the dependency runs one way, so they travel up
   * rather than being read back down. */
  setAssetSwaps: (swaps: WalletAssetSwap[]) => void
  vtxos: { spendable: Vtxo[]; spent: Vtxo[] }
  balance: WalletBalance['total']
  availableBalance: WalletBalance['available']
  /** Everything the wallet owns, including assets escrowed in a swap covenant,
   * intent-locked or awaiting recovery. Reporting only. */
  assetBalances: WalletBalance['assets']
  /** The subset generic spending will accept — the asset analogue of
   * `availableBalance`. Any selectable amount must come from here. */
  availableAssetBalances: WalletBalance['availableAssets']
  assetMetadataCache: Map<string, CachedAssetDetails>
  setCacheEntry: (assetId: string, details: AssetDetails) => CachedAssetDetails
  iconApprovalManager: AssetIconApprovalManager
  isVerifiedAsset: (assetId: string) => boolean
  dataReady: boolean
  /** The worker's first contract sync since setup has not landed: balances and history are stored data. */
  syncing?: boolean
  loadError: string | null
  dismissLoadError: () => void
  authState: WalletAuthState
  initialized?: boolean
  devAutoInitFailed?: boolean
}

export const WalletContext = createContext<WalletContextProps>({
  initWallet: () => Promise.resolve(),
  lockWallet: () => Promise.resolve(),
  resetWallet: () => Promise.resolve(),
  settlePreconfirmed: () => Promise.resolve(),
  unlockWallet: () => Promise.resolve(),
  updateWallet: () => {},
  reloadWallet: () => Promise.resolve(),
  restartWallet: () => Promise.resolve(),
  wallet: defaultWallet,
  walletLoaded: false,
  svcWallet: undefined,
  vtxoManager: undefined,
  isLocked: () => Promise.resolve(true),
  balance: 0,
  availableBalance: 0,
  assetBalances: [],
  availableAssetBalances: [],
  assetMetadataCache: new Map(),
  setCacheEntry: () => ({ cachedAt: 0 }) as CachedAssetDetails,
  iconApprovalManager: new AssetIconApprovalManager(),
  isVerifiedAsset: () => false,
  dataReady: false,
  syncing: false,
  loadError: null,
  dismissLoadError: () => {},
  authState: 'unknown',
  txs: [],
  ungroupedTxs: [],
  setAssetSwaps: () => {},
  vtxos: { spendable: [], spent: [] },
  devAutoInitFailed: false,
})

/** The asset legs stored swap records mention.
 *
 * Read straight from the repository rather than from this provider's
 * `assetSwaps` state: that state is published upward by `AssetSwapsProvider`
 * and is still empty on the reload that renders the first history, which is
 * exactly the reload whose rows need naming. A read failure yields no ids
 * rather than failing the reload — an unnamed row beats no wallet. */
const readSwapRecordAssets = async (): Promise<string[]> => {
  try {
    const swaps = (await getAssetSwaps(assetSwapRepository)) as WalletAssetSwap[]
    return swaps.flatMap((swap) => [swap.fromAsset, swap.toAsset])
  } catch (err) {
    consoleError(err, 'failed to read swap records while prefetching asset metadata')
    return []
  }
}

export const WalletProvider = ({ children }: { children: ReactNode }) => {
  const { aspInfo } = useContext(AspContext)
  const { isRegistered } = useContext(AssetsContext)
  const { initialiseNostrBackup } = useContext(BackupContext)
  const { config, updateConfig } = useContext(ConfigContext)
  const { navigate } = useContext(NavigationContext)
  const { setNoteInfo, noteInfo, setDeepLinkInfo, deepLinkInfo } = useContext(FlowContext)
  const { notifyTxSettled } = useContext(NotificationsContext)
  const { t } = useTranslation()

  // One atomic snapshot: the metadata graft must land in the same render as
  // the history it belongs to.
  const [history, setHistory] = useState<{
    activities: Activity[]
    metadata: Record<string, TransactionActivityMetadata>
    lnSends: LnSendView[]
    exits: ExitRecord[]
  }>({ activities: [], metadata: {}, lnSends: [], exits: [] })
  const [assetSwaps, setAssetSwaps] = useState<WalletAssetSwap[]>([])
  const [balance, setBalance] = useState(0)
  const [availableBalance, setAvailableBalance] = useState(0)
  const [wallet, setWallet] = useState(() => readWalletFromStorage() ?? defaultWallet)
  const walletLoaded = true
  const [initialized, setInitialized] = useState<boolean>(false)
  const [svcWallet, setSvcWallet] = useState<ServiceWorkerWallet>()
  const [dataReady, setDataReady] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [authState, setAuthState] = useState<WalletAuthState>('unknown')
  const [vtxos, setVtxos] = useState<{ spendable: Vtxo[]; spent: Vtxo[] }>({ spendable: [], spent: [] })
  const [assetBalances, setAssetBalances] = useState<WalletBalance['assets']>([])
  const [availableAssetBalances, setAvailableAssetBalances] = useState<WalletBalance['availableAssets']>([])

  const [vtxoManager, setVtxoManager] = useState<IVtxoManager>()

  const hasLoadedOnce = useRef(false)
  const assetMetadataCache = useRef<Map<string, CachedAssetDetails>>(readAssetMetadataFromStorage() ?? new Map())
  // Every asset the UI can still be asked to name (see `referencedAssetIds`).
  // A ref because it gates persistence in `setCacheEntry`, not rendering.
  const referencedAssetIdsRef = useRef<Set<string>>(new Set())
  const iconApprovalManager = useRef(new AssetIconApprovalManager()).current

  // Rows name assets through `assetMetadataCache`, which is a ref, so filling it
  // repaints nothing on its own. The metadata prefetch answers over the network
  // and routinely lands after the first history load, and a restored wallet has
  // an empty cache to start with, so every asset row rendered its truncated id
  // until some unrelated change happened to rebuild this memo — which is why
  // making one new swap named every older row at once. Bumped by `setCacheEntry`
  // when it writes a name that differs from the one already cached.
  const [assetDisplayVersion, setAssetDisplayVersion] = useState(0)

  // Derived rather than merged once at load: the swap records are read from
  // IndexedDB, so they can arrive after the first history load — recomputing on
  // either input is what keeps a cold start from flashing bare funding rows.
  const txs = useMemo(
    () =>
      activitiesToTxs(history.activities, {
        swaps: assetSwaps,
        metadata: history.metadata,
        lnSends: history.lnSends,
        exits: history.exits,
        network: aspInfo.network,
        assetDisplay: (id) => assetMetadataCache.current.get(id)?.metadata,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [history, assetSwaps, aspInfo.network, assetDisplayVersion],
  )

  const ungroupedTxs = useMemo(
    () => history.activities.flatMap((activity) => activity.txs).map((tx) => arkTransactionToTx(tx)),
    [history],
  )

  const verifiedAssetsFetched = useRef(false)
  const statusPingInterval = useRef<ReturnType<typeof setInterval>>()
  const reloadTimerRef = useRef<ReturnType<typeof setTimeout>>()
  const lnurlResyncRef = useRef<ReturnType<typeof lnurlResyncOnPayment>>()
  const swMessageHandlerRef = useRef<(event: MessageEvent) => void>()
  const reinitInProgress = useRef(false)
  const initAbortRef = useRef<AbortController | null>(null)
  const reinitSvcWalletRef = useRef<((identity: Identity) => Promise<void>) | null>(null)
  // reloadWallet runs from long-lived listeners (service-worker messages, the
  // swap SSE monitor) whose closures captured an early `config` — theme still
  // the default Themes.Auto. Read the live config through a ref so the assets-
  // app auto-enable write below never spreads a stale snapshot and resets the
  // user's saved theme (applyTheme(Auto) then falls back to the OS palette).
  const configRef = useRef(config)
  configRef.current = config
  // Same hazard: a listener that captured an early `aspInfo` captured it before
  // the server answered, when the network was still unset. The exit timestamp
  // lookup needs the live one to pick an explorer. Synced after commit rather
  // than during render, so the ref only ever holds a network React actually
  // rendered with — a render that gets discarded must not leave its network
  // behind for `reloadWallet` to pick an explorer from. Declared here, above
  // every effect that reloads, so it is the first to run on the commit that
  // brings the network in.
  const networkRef = useRef(aspInfo.network)
  useEffect(() => {
    networkRef.current = aspInfo.network
  }, [aspInfo.network])

  // Each init gets its own AbortSignal; lock/reset aborts the current signal
  // with 'lock-reset' so stale paths can decide whether to tear down the SW.
  // A new init aborts the previous with 'init', which means "abandon, don't clear".
  const startInitSession = (): AbortSignal => {
    initAbortRef.current?.abort('init')
    initAbortRef.current = new AbortController()
    return initAbortRef.current.signal
  }

  const abortInitSession = () => {
    initAbortRef.current?.abort('lock-reset')
    initAbortRef.current = null
  }

  const clearIfLockReset = async (svcWallet: ServiceWorkerWallet, signal: AbortSignal) => {
    if (!signal.aborted || signal.reason !== 'lock-reset') return
    try {
      await svcWallet.clear()
    } catch (err) {
      consoleError(err, 'Error clearing stale service worker wallet')
    }
  }

  const removeServiceWorkerMessageHandler = (handler = swMessageHandlerRef.current) => {
    if (!handler) return
    navigator.serviceWorker.removeEventListener('message', handler)
    if (swMessageHandlerRef.current === handler) swMessageHandlerRef.current = undefined
  }

  // Currency identity (flags, fiat rates, fiat-style formatting) must be pinned to
  // asset IDs present in a curated list — never inferred from self-reported tickers,
  // which anyone can mint.
  const isVerifiedAsset = (assetId: string): boolean =>
    Boolean(assetId) && (iconApprovalManager.isVerified(assetId) || isRegistered(assetId))

  const setCacheEntry = (assetId: string, details: AssetDetails): CachedAssetDetails => {
    const hasIcon = !!details.metadata?.icon
    const moderated =
      hasIcon && (!iconApprovalManager.isApproved(assetId) || !isValidUrl(details.metadata?.icon ?? ''))
        ? { ...details, metadata: { ...details.metadata, icon: undefined } }
        : details
    const entry: CachedAssetDetails = { ...moderated, cachedAt: Date.now(), hasIcon }
    const previous = assetMetadataCache.current.get(assetId)
    assetMetadataCache.current.set(assetId, entry)
    saveAssetMetadataToStorage(assetMetadataCache.current, referencedAssetIdsRef.current)
    // Only what `assetDisplay` reads is worth a repaint. A TTL refresh rewriting
    // the same name must not re-derive every row, and the prefetch loop writes
    // one entry per owned asset with an await between each.
    if (assetNameChanged(previous?.metadata, entry.metadata)) setAssetDisplayVersion((version) => version + 1)
    return entry
  }

  // wallet is read synchronously in useState initializer above

  const devMnemonic = import.meta.env.VITE_DEV_MNEMONIC as string | undefined
  const devNsec = import.meta.env.VITE_DEV_NSEC as string | undefined
  const isDevAutoInit = import.meta.env.DEV && (Boolean(devMnemonic) || Boolean(devNsec))
  const [devAutoInitFailed, setDevAutoInitFailed] = useState(false)

  // dev-only: auto-initialize wallet from VITE_DEV_MNEMONIC / VITE_DEV_NSEC, bypassing onboarding and unlock
  useEffect(() => {
    if (!isDevAutoInit || devAutoInitFailed) return
    if (initialized) return
    if (!aspInfo.url) return

    let cancelled = false
    let watchdog: ReturnType<typeof setTimeout> | undefined

    const autoInit = async () => {
      try {
        watchdog = setTimeout(() => {
          if (cancelled) return
          consoleError(new Error('Dev wallet auto-init timed out'), 'Dev auto-init watchdog')
          try {
            if (sessionStorage.getItem(DEV_AUTO_INIT_RELOAD_KEY)) {
              setDevAutoInitFailed(true)
              return
            }
            sessionStorage.setItem(DEV_AUTO_INIT_RELOAD_KEY, 'true')
          } catch {
            // keep recovering even if session storage is unavailable
          }
          navigator.serviceWorker
            ?.getRegistration()
            .then((reg) => reg?.unregister())
            .finally(() => window.location.reload())
        }, DEV_AUTO_INIT_TIMEOUT_MS)
        if (cancelled) return
        clearTimeout(watchdog)
        try {
          sessionStorage.removeItem(DEV_AUTO_INIT_RELOAD_KEY)
        } catch {
          // ignore session storage errors
        }
        if (devMnemonic) await initWallet({ mnemonic: devMnemonic })
        else if (devNsec) await initWallet({ privateKey: nsecToPrivateKey(devNsec) })
        setAuthState('authenticated')
      } catch (err) {
        clearTimeout(watchdog)
        if (cancelled) return
        consoleError(err, 'Dev auto-init failed')
        setDevAutoInitFailed(true)
      }
    }

    autoInit()
    return () => {
      cancelled = true
      clearTimeout(watchdog)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aspInfo.url, initialized, devAutoInitFailed, isDevAutoInit, devMnemonic, devNsec])

  useEffect(() => {
    // skip auth check when dev auto-init will handle it
    if (isDevAutoInit && !devAutoInitFailed) {
      if (!initialized) return
      setAuthState('authenticated')
      return
    }

    if (!wallet.pubkey) {
      setAuthState('authenticated')
      return
    }

    let cancelled = false
    setAuthState('unknown')

    const detectPasswordState = async () => {
      if (hasMnemonic()) {
        try {
          await getMnemonic(defaultPassword)
          return true // passwordless
        } catch {
          return false // has custom password
        }
      }
      return noUserDefinedPassword()
    }

    detectPasswordState()
      .then((noPassword) => {
        if (!cancelled) setAuthState(noPassword ? 'passwordless' : 'locked')
      })
      .catch(() => {
        if (!cancelled) setAuthState('locked')
      })

    return () => {
      cancelled = true
    }
  }, [wallet.pubkey])

  // History is loaded after `client.ready` (see SwapsProvider). Skip here when
  // url and network are set — that is the path that starts the swap client.
  // This branch is not first unlock: `initWallet` already had a server URL.
  useEffect(() => {
    if (!svcWallet) return
    if (!aspInfo.url || !aspInfo.network) reloadWallet().catch(consoleError)
  }, [svcWallet, aspInfo.url, aspInfo.network])

  useEffect(() => {
    if (!import.meta.env.DEV || !isDevAutoInit) return
    if (!initialized || dataReady || loadError) return

    const timer = window.setTimeout(() => {
      if (hasLoadedOnce.current) return
      consoleError(new Error('Dev initial wallet data load timed out'), 'Dev initial data watchdog')
      hasLoadedOnce.current = true
      setDataReady(true)
    }, DEV_INITIAL_DATA_TIMEOUT_MS)

    return () => window.clearTimeout(timer)
  }, [initialized, dataReady, loadError, isDevAutoInit])

  // calculate thresholdMs and next rollover
  useEffect(() => {
    if (!initialized || !vtxos || !svcWallet) return
    const computeThresholds = async () => {
      try {
        const allVtxos = await svcWallet.getVtxos({ withRecoverable: true })
        const batchLifetimeMs = await calcBatchLifetimeMs(allVtxos, new Indexer(aspInfo))
        const thresholdMs = Math.floor((batchLifetimeMs * maxPercentage) / 100)
        const nextRollover = await calcNextRollover(vtxos.spendable, svcWallet, aspInfo)
        updateWallet((prev) => ({ ...prev, nextRollover, thresholdMs }))
      } catch (err) {
        consoleError(err, 'Error computing rollover thresholds')
      }
    }
    computeThresholds()
  }, [initialized, vtxos, svcWallet, aspInfo])

  // fetch verified assets list once on startup
  useEffect(() => {
    const verifiedUrl = import.meta.env.VITE_VERIFIED_ASSETS_URL
    if (!verifiedUrl || verifiedAssetsFetched.current) return
    if (!initialized) return
    verifiedAssetsFetched.current = true

    fetch(verifiedUrl)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.json()
      })
      .then((data) => {
        if (!Array.isArray(data) || !data.every((id) => typeof id === 'string')) {
          throw new Error('Invalid verified assets response')
        }
        iconApprovalManager.setVerifiedAssets(data)
      })
      .catch((err) => consoleError(err, 'Failed to fetch verified assets'))
  }, [initialized])

  // if ark note is present in the URL, decode it and set the note info
  useEffect(() => {
    const dlInfo = deepLinkInUrl()
    if (dlInfo) {
      setDeepLinkInfo(dlInfo)
    }
    const note = arkNoteInUrl()
    if (note) {
      try {
        const { value } = ArkNote.fromString(note)
        setNoteInfo({ note, satoshis: value })
      } catch (err) {
        consoleError(err, 'error decoding ark note ')
      }
    }
    window.location.hash = ''
  }, [])

  useEffect(() => {
    // Precedence is given to NoteInfo, but they are mutually exclusive because depend on window.location.hash
    if (!initialized || !dataReady) return
    if (noteInfo.satoshis) {
      // if voucher present, go to redeem page
      navigate(Pages.NotesRedeem)
      return
    }
    // if app url is present, navigate to it
    if (!deepLinkInfo?.appId) return
    switch (deepLinkInfo?.appId) {
      default:
        navigate(Pages.Wallet)
    }
  }, [initialized, dataReady, noteInfo.satoshis, deepLinkInfo])

  const reloadWallet = async (swWallet = svcWallet) => {
    if (!swWallet) return
    const isFirstLoad = !hasLoadedOnce.current
    if (isFirstLoad) setLoadError(null)
    try {
      if (isFirstLoad) setLoadingStatus(t('loading.fetchingCoins'))
      const vtxos = await getVtxos(swWallet)
      // Fetched apart from the set above, which must not learn about exits —
      // see `getUnrolledVtxos`. Cheap: the worker answers both from its local
      // repo, so this is a postMessage, not a request.
      const unrolledVtxos = await getUnrolledVtxos(swWallet)
      if (isFirstLoad) setLoadingStatus(t('loading.fetchingTransactions'))
      const activities = await getActivities(swWallet)
      // Before the metadata snapshot below, not after: `resolveExits` persists
      // what it learns, and the history memo reads a snapshot taken here, so a
      // write that lands after this line stays invisible until the next reload.
      const exits = await resolveExits(unrolledVtxos, networkRef.current)
      const metadata = readAllTransactionActivityMetadata()
      // Read, never resolved here: the swap client owns a send's outcome and
      // has already written it (see providers/swaps), so this pass only picks
      // up what the store says.
      const lnSends = await lnSendViews()
      if (isFirstLoad) setLoadingStatus(t('loading.updatingBalance'))
      const { total, available, assets, availableAssets, unrolled } = await getBalance(swWallet)
      // An exited coin is no longer Arkade money: it cannot be spent offchain,
      // no batch can lift it back, and this wallet has no path that moves it —
      // the sweep belongs to the exit tool. The SDK's `total` is right for what
      // it claims (every sat the wallet owns), but this headline means the
      // narrower thing, so the bucket is netted out at the display boundary and
      // the exit is explained by a history row instead. `available` already
      // excludes it, so Send, Swap and coin selection need nothing.
      const ownedAssets = subtractExitedAssets(assets, unrolledVtxos)
      // Naming is not a property of the balance sheet. A row names an asset
      // long after the wallet stops holding it — swap the last of an asset away
      // and its swap rows still have to say what was traded — so the prefetch
      // covers every asset the UI can reference, not just the owned ones. Only
      // the owned list was covered before, which is why such a row fell back to
      // a truncated asset id with a letter where its logo belongs, and why the
      // fall happened a day late: the entry survived until the TTL evicted it.
      const referenced = referencedAssetIds({
        owned: ownedAssets,
        rows: activities.flatMap((activity) =>
          activity.txs.flatMap((tx) => (arkTransactionToTx(tx).assets ?? []).map((asset) => asset.assetId)),
        ),
        swaps: await readSwapRecordAssets(),
      })
      referencedAssetIdsRef.current = referenced
      if (isFirstLoad && referenced.size > 0) setLoadingStatus(t('loading.loadingAssetMetadata'))
      for (const assetId of referenced) {
        const cached = assetMetadataCache.current.get(assetId)
        if (cached && Date.now() - cached.cachedAt < ASSET_METADATA_TTL_MS) continue
        try {
          const meta = await swWallet.assetManager.getAssetDetails(assetId)
          if (meta) setCacheEntry(assetId, meta)
        } catch (err) {
          consoleError(err, `error prefetching metadata for ${assetId}`)
        }
      }
      setBalance(total - unrolled)
      setAvailableBalance(available)
      setAssetBalances(ownedAssets)
      setAvailableAssetBalances(availableAssets)
      if (ownedAssets.length > 0 && !configRef.current.apps.assets.enabled) {
        const live = configRef.current
        updateConfig({ ...live, apps: { ...live.apps, assets: { enabled: true } } })
      }
      setVtxos(vtxos)
      setHistory({ activities, metadata, lnSends, exits })
      if (!hasLoadedOnce.current) {
        hasLoadedOnce.current = true
        setDataReady(true)
      }
    } catch (err) {
      consoleError(err, 'Error reloading wallet')
      if (!hasLoadedOnce.current) {
        setLoadError('Unable to load wallet data. Check your connection and try again.')
      }
    }
  }

  const dismissLoadError = () => {
    setLoadError(null)
    hasLoadedOnce.current = true
    setDataReady(true)
  }

  const initSvcWorkerWallet = async (params: InitSvcWorkerWalletParams): Promise<boolean> => {
    const signal = startInitSession()
    return runInitAttempt(signal, params.identity, params)
  }

  // Retries inherit the signal minted by the top-level call so a lock/reset
  // that fires between retries is observable via signal.aborted.
  const runInitAttempt = async (
    signal: AbortSignal,
    identity: Identity,
    params: InitSvcWorkerWalletParams,
  ): Promise<boolean> => {
    const { arkServerUrl, retryCount = 0, maxRetries = 2, delegateUrl, walletMode, restoring = false } = params
    try {
      setLoadingStatus(t('loading.startingWallet'))
      const walletRepository = new IndexedDBWalletRepository()
      const contractRepository = new IndexedDBContractRepository()

      // Zombie SW detection and IndexedDB warmup are independent — run them
      // concurrently. The zombie ping timeout is 500ms: alive workers respond
      // in <10ms, so anything slower is dead.
      const zombieCheck = (async () => {
        const existingReg = await navigator.serviceWorker.getRegistration()
        const active = existingReg?.active
        if (active) {
          const alive = await new Promise<boolean>((resolve) => {
            const channel = new MessageChannel()
            const timer = setTimeout(() => {
              channel.port1.close()
              resolve(false)
            }, 500)
            channel.port1.onmessage = (event) => {
              clearTimeout(timer)
              channel.port1.close()
              resolve(event.data?.type === 'PONG')
            }
            active.postMessage({ type: 'PING' }, [channel.port2])
          })
          if (!alive) {
            await existingReg.unregister()
          }
        }
      })()

      await Promise.all([walletRepository.getWalletState(), zombieCheck])
      setLoadingStatus(t('loading.connectingToServiceWorker'))

      const svcWallet = await ServiceWorkerWallet.setup({
        serviceWorkerPath: '/wallet-service-worker.mjs',
        identity,
        arkServer: { url: arkServerUrl },
        delegateUrl,
        walletMode: walletMode ?? config.walletMode ?? 'static',
        // Contract sync finishes in the background; the coin-event reload below repaints. Not lazyBoarding: its
        // refresh is never retried, and its UTXO_UPDATE carries every stored coin, which the receive screen reads as a payment.
        lazyInitialization: true,
        storage: { walletRepository, contractRepository },
        serviceWorkerActivationTimeoutMs: SERVICE_WORKER_ACTIVATION_TIMEOUT_MS,
        messageBusTimeoutMs: MESSAGE_BUS_INIT_TIMEOUT_MS,
        messageTimeouts: {
          SETTLE: 60_000,
          SEND: 60_000,
        },
        settlementConfig: { vtxoThreshold: wallet.thresholdMs ? Math.floor(wallet.thresholdMs / 1000) : 1 },
      })

      // The first data load waits on the swap client (see SwapsProvider), so
      // the worker is no longer what the screen is waiting on.
      setLoadingStatus(t('loading.loadingWalletData'))

      // The registry ships with the SDK built-ins already in it; only ours has
      // to be added, and `use()` is idempotent by id across reinit paths. Both
      // families over the client's own records, which is now all of them. Its
      // corridor half is what labels a Lightning row and gives it the outcome
      // token the receipt renders — turning a swap's funding tx, and the claim
      // or refund that follows it, into one labelled activity rather than two
      // unrelated rows.
      // The reader, not `getVtxos()`, which drops spent coins — and a claimed
      // lockup is spent. Needs no client lock, so a passive tab still groups.
      svcWallet.activity.use(
        swapRecordResolver(undefined, async (script) => {
          const reader = await svcWallet.getArkadeReader()
          const { vtxos } = await reader.getVtxos({ scripts: [script] })
          return vtxos
        }),
      )
      svcWallet.activity.use(createLnurlActivityResolver())
      svcWallet.activity.use(createSentActivityResolver())

      // Before `setSvcWallet`: the swap client is built only once the wallet is
      // set, and its construction restore rebuilds swaps from the history this
      // fills in. Swaps are not restored here — that is the client's job.
      if (restoring) {
        setLoadingStatus(t('loading.recoveringAddresses'))
        try {
          await svcWallet.restore()
        } catch (err) {
          consoleError(err, 'Error scanning for rotated addresses on restore')
        }
      }

      const vtxoMgr = await svcWallet.getVtxoManager()
      const { walletInitialized } = await svcWallet.getStatus()

      // Single checkpoint before any state/listener commits. Migration and
      // getVtxoManager/getStatus are side-effect-free w.r.t. React/DOM, so
      // running them after an abort is wasteful but safe.
      if (signal.aborted) {
        await clearIfLockReset(svcWallet, signal)
        return false
      }

      setSvcWallet(svcWallet)
      setVtxoManager(vtxoMgr)
      setInitialized(walletInitialized)
      setSyncing(true)

      // Safe on every unlock: watches are keyed by txid and the confirmation queue ignores a key it already holds.
      resumeLnurlConfirmations()
      // Receives that completed while the wallet was closed are the ones it can
      // never witness first-hand, so pull them once the wallet is usable.
      // Deliberately not awaited: an unreachable lnurl-server must cost the
      // activity view its attribution, never the wallet its startup.
      const syncLnurl = async () => {
        const arkadeAddress = await svcWallet.getAddress()
        const boardingAddress = await svcWallet.getBoardingAddress().catch(() => undefined)
        const { synced, failures } = await syncLnurlActivity(identity, arkadeAddress, { boardingAddress, signal })
        if (failures.length) consoleError(failures, 'lnurl activity sync failed')
        return synced
      }
      const reloadAfterSync = () => {
        if (!signal.aborted) reloadWallet(svcWallet).catch(consoleError)
      }
      syncLnurl()
        .then((synced) => {
          if (synced > 0) reloadAfterSync()
        })
        .catch((error) => {
          consoleError(error, 'lnurl activity sync failed')
        })

      // Cancel any pending reload from a previous wallet instance
      clearTimeout(reloadTimerRef.current)
      lnurlResyncRef.current?.cancel()
      lnurlResyncRef.current = lnurlResyncOnPayment(syncLnurl, reloadAfterSync)

      const scheduleReload = () => {
        // Debounced reload: short delay lets the indexer update its cache.
        // If multiple updates arrive in quick succession, only the last
        // one triggers a reload (avoids redundant fetches).
        clearTimeout(reloadTimerRef.current)
        reloadTimerRef.current = setTimeout(() => reloadWallet(svcWallet), 1000)
      }

      // handle messages from the service worker
      // we listen for UTXO/VTXO updates to refresh the tx history and balance
      const handleServiceWorkerMessages = (event: MessageEvent) => {
        const { type, payload } = event.data ?? {}
        // A lazy boot's first sync can land before VTXO_UPDATE's worker listener exists; CONTRACT_EVENT's is set up first.
        const coinEvent = type === 'CONTRACT_EVENT' && ['vtxo_received', 'vtxo_spent'].includes(payload?.event?.type)
        if (coinEvent || ['VTXO_UPDATE', 'UTXO_UPDATE'].includes(type)) {
          scheduleReload()
          lnurlResyncRef.current?.onPayment()
        }
      }

      // Until the lazy boot's first sync lands the screen shows stored data, and a coin whose status
      // changed in place (preconfirmed to settled) raises no coin event, so reload once it has landed.
      let awaitingFirstSync = true
      const checkFirstSync = async () => {
        try {
          const { lastSyncedAt } = (await svcWallet.getContractManager()).getSyncState()
          if (!lastSyncedAt || signal.aborted) return
          awaitingFirstSync = false
          setSyncing(false)
          scheduleReload()
        } catch (err) {
          consoleError(err, 'Error reading contract sync state')
        }
      }

      if (swMessageHandlerRef.current) {
        removeServiceWorkerMessageHandler(swMessageHandlerRef.current)
      }
      // Late is safe with a lazy boot: the worker persists coins before announcing them, and the first reload runs after this.
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessages)
      swMessageHandlerRef.current = handleServiceWorkerMessages

      // ping the service worker wallet status every 1 second
      if (statusPingInterval.current) clearInterval(statusPingInterval.current)
      let consecutivePingFailures = 0
      let pingInProgress = false
      statusPingInterval.current = setInterval(async () => {
        if (pingInProgress) return
        pingInProgress = true
        try {
          const statusTimeout = new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('status ping timed out')), 3_000),
          )
          const { walletInitialized } = await Promise.race([svcWallet.getStatus(), statusTimeout])
          consecutivePingFailures = 0
          if (!signal.aborted) setInitialized(walletInitialized)
          if (awaitingFirstSync) await checkFirstSync()
        } catch (err) {
          consoleError(err, 'Error pinging wallet status')
          consecutivePingFailures++
          // Guard with signal so a stale in-flight ping from a dead session
          // cannot clear the new session's interval or trigger a reinit.
          if (consecutivePingFailures >= 3 && !signal.aborted) {
            clearInterval(statusPingInterval.current)
            reinitSvcWalletRef.current?.(identity)
          }
        } finally {
          pingInProgress = false
        }
      }, 1_000)

      // Renew expiring coins on startup (non-delegate mode only).
      // When delegation is enabled, the SDK's VtxoManager auto-delegates
      // via onContractEvent, so no wallet-side call is needed.
      if (!config.delegate) {
        vtxoMgr.renewVtxos().catch(() => {})
      }
      return true
    } catch (err) {
      if (signal.aborted) return false

      const isTimeoutError =
        err instanceof Error &&
        (err.message.includes('Service worker activation timed out') || err.message.includes('MessageBus timed out'))

      if (isTimeoutError && retryCount < maxRetries) {
        // exponential backoff: wait 1s, 2s, 4s, 8s, 16s for each retry
        const delay = Math.pow(2, retryCount) * 1000
        setLoadingStatus(t('loading.retryingConnection'))
        consoleError(
          new Error(
            `Service worker activation timed out, retrying in ${delay}ms (attempt ${retryCount + 1}/${maxRetries})`,
          ),
          'Service worker activation retry',
        )
        await new Promise((resolve) => setTimeout(resolve, delay))
        if (signal.aborted) return false
        return runInitAttempt(signal, identity, { ...params, retryCount: retryCount + 1 })
      }

      // If we are here, either retries are exhausted or it's a different error.
      // When the SW is permanently unresponsive (all retries exhausted), unregister
      // it so the next page load gets a fresh registration instead of reusing the
      // broken activation. This makes the one-time reload recovery effective.
      if (isTimeoutError && retryCount >= maxRetries) {
        try {
          const reg = await navigator.serviceWorker.getRegistration()
          if (reg) await reg.unregister()
        } catch {
          // best-effort cleanup
        }
      }

      // Surface the failure so the unlock flow cannot proceed silently without an initialized wallet.
      throw err
    }
  }

  const initWallet = async (credentials: {
    mnemonic?: string
    privateKey?: Uint8Array
    walletMode?: ServiceWorkerWalletMode
    restoring?: boolean
  }) => {
    const arkServerUrl = aspInfo.url
    const network = aspInfo.network as NetworkName

    let identity: Identity
    let pubkey: string
    let walletMode: ServiceWorkerWalletMode

    const delegateUrl = config.delegate ? getDelegateUrlForNetwork(network) : undefined

    if (credentials.mnemonic) {
      const mnemonicIdentity = MnemonicIdentity.fromMnemonic(credentials.mnemonic, { isMainnet: isMainnet(network) })
      identity = mnemonicIdentity
      pubkey = hex.encode(await mnemonicIdentity.compressedPublicKey())
      // HD-capable: honor the requested mode (creation) or the persisted mode (unlock)
      walletMode = resolveWalletMode({
        hasMnemonic: true,
        requested: credentials.walletMode,
        persisted: config.walletMode,
      })
      initialiseNostrBackup(deriveNostrKeyFromMnemonic(credentials.mnemonic, isMainnet(network)))
      updateConfig({ ...config, pubkey, walletMode })
    } else if (credentials.privateKey) {
      identity = SingleKey.fromPrivateKey(credentials.privateKey)
      pubkey = hex.encode(secp256k1.getPublicKey(credentials.privateKey))
      walletMode = 'static'
      initialiseNostrBackup(credentials.privateKey)
      updateConfig({ ...config, pubkey, walletMode })
    } else {
      throw new Error('Either mnemonic or privateKey must be provided')
    }

    const didInit = await initSvcWorkerWallet({
      identity,
      arkServerUrl,
      delegateUrl,
      walletMode,
      restoring: credentials.restoring,
    })
    if (!didInit) return
    updateWallet({ ...wallet, network, pubkey })
    setInitialized(true)
  }

  const unlockWallet = async (password: string) => {
    try {
      if (hasMnemonic()) {
        const mnemonic = await getMnemonic(password)
        setAuthState('authenticated')
        await initWallet({ mnemonic })
      } else {
        const privateKey = await getPrivateKey(password)
        setAuthState('authenticated')
        await initWallet({ privateKey })
      }
    } catch (err) {
      setAuthState('locked')
      if (err instanceof DOMException) throw new Error('Invalid password')
      throw err instanceof Error ? err : new Error('Invalid password')
    }
  }

  /**
   * Reinitialize the service-worker wallet in-place so runtime config changes
   * (e.g., delegate on/off) take effect without forcing a lock/unlock cycle.
   * Keeps local tx/balance state; just rebuilds the SW wallet with the current
   * delegateUrl flag.
   */
  const restartWallet = async (delegateEnabled = config.delegate) => {
    if (!svcWallet) return
    const identity = svcWallet.identity as Identity
    const arkServerUrl = aspInfo.url
    const delegateUrl = delegateEnabled ? getDelegateUrlForNetwork(aspInfo.network as NetworkName) : undefined
    await initSvcWorkerWallet({
      identity,
      arkServerUrl,
      delegateUrl,
    })
  }

  // Self-heal when the SW dies: re-run setup with the existing identity so
  // SwapsProvider and other consumers re-bind via setSvcWallet, instead of
  // reloading the page (which would discard FlowProvider state and UI context).
  // Identity is passed in by the caller because the setInterval that triggers
  // reinit captures this closure from the render before svcWallet state was
  // populated.
  const reinitSvcWallet = async (identity: Identity) => {
    if (reinitInProgress.current) return
    reinitInProgress.current = true
    try {
      const arkServerUrl = aspInfo.url
      const delegateUrl = config.delegate ? getDelegateUrlForNetwork(aspInfo.network as NetworkName) : undefined
      const initialized = await initSvcWorkerWallet({
        identity,
        arkServerUrl,
        delegateUrl,
      })
      if (!initialized) return
    } catch (err) {
      consoleError(err, 'SW reinit failed; falling back to full reload')
      window.location.reload()
    } finally {
      reinitInProgress.current = false
    }
  }

  useEffect(() => {
    reinitSvcWalletRef.current = reinitSvcWallet
  })

  const lockWallet = async () => {
    abortInitSession()
    if (!svcWallet) throw new Error('Service worker not initialized')
    if (statusPingInterval.current) clearInterval(statusPingInterval.current)
    statusPingInterval.current = undefined
    clearTimeout(reloadTimerRef.current)
    reloadTimerRef.current = undefined
    lnurlResyncRef.current?.cancel()
    removeServiceWorkerMessageHandler()
    await svcWallet.clear()
    setAuthState('locked')
    setInitialized(false)
    setDataReady(false)
    hasLoadedOnce.current = false
  }

  const resetWallet = async () => {
    // Aborting the session stops the LNURL history sync writing, so the purge below has nothing racing it.
    abortInitSession()
    lnurlResyncRef.current?.cancel()
    if (!svcWallet) throw new Error('Service worker not initialized')
    // Before anything destructive, and fatal: these records hold preimages, so a
    // reset that completed without them gone would hand the next wallet the
    // previous one's payment history. Rejecting here leaves the wallet intact,
    // and still showing balance updates, as the listeners stop only after it.
    await clearLnurlPaymentsForReset()
    if (statusPingInterval.current) clearInterval(statusPingInterval.current)
    statusPingInterval.current = undefined
    clearTimeout(reloadTimerRef.current)
    reloadTimerRef.current = undefined
    removeServiceWorkerMessageHandler()
    forgetLnurlSends()
    await clearStorage()
    // swap records outlive localStorage now: without this a reset leaves the
    // previous wallet's swaps in the activity list. Never fatal — a reset that
    // aborted here would leave the wallet itself half-cleared, which is worse
    // than stale swap rows.
    await assetSwapRepository.clear().catch((err) => consoleError(err, 'failed to clear swap records'))
    setAssetSwaps([])
    await svcWallet.clear()
    await svcWallet.walletRepository.clear()
    await svcWallet.contractRepository.clear()
    setDataReady(false)
    hasLoadedOnce.current = false
  }

  const settlePreconfirmed = async () => {
    if (!svcWallet || !vtxoManager) throw new Error('Service worker not initialized')
    await settleVtxos(svcWallet, vtxoManager, aspInfo.dust, wallet.thresholdMs, aspInfo.fees?.intentFee)
    notifyTxSettled()
  }

  const updateWallet = (data: Wallet | ((prev: Wallet) => Wallet)) => {
    setWallet((prev) => {
      const next = typeof data === 'function' ? (data as (prev: Wallet) => Wallet)(prev) : data
      saveWalletToStorage(next)
      return { ...next }
    })
  }

  const isLocked = async () => {
    if (!svcWallet) return true
    try {
      const { walletInitialized } = await svcWallet.getStatus()
      return !walletInitialized
    } catch {
      return true
    }
  }

  return (
    <WalletContext.Provider
      value={{
        authState,
        initWallet,
        isLocked,
        initialized,
        resetWallet,
        settlePreconfirmed,
        unlockWallet,
        updateWallet,
        wallet,
        walletLoaded,
        svcWallet,
        vtxoManager,
        lockWallet,
        restartWallet,
        txs,
        ungroupedTxs,
        setAssetSwaps,
        balance,
        availableBalance,
        assetBalances,
        availableAssetBalances,
        assetMetadataCache: assetMetadataCache.current,
        setCacheEntry,
        iconApprovalManager,
        isVerifiedAsset,
        dataReady,
        syncing,
        loadError,
        dismissLoadError,
        reloadWallet,
        devAutoInitFailed,
        vtxos: vtxos ?? { spendable: [], spent: [] },
      }}
    >
      {children}
    </WalletContext.Provider>
  )
}
